/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from "@emotion/core";
import type { FC } from "react";
import { useState, useMemo, useEffect } from "react";
import {
  type DropdownStatusDefault,
  type CommentChannel,
  type AdjudicationStatus,
  type ConversationThread,
  CommentObjectTypes
} from "@certa/types";
import { useCommentsContext } from "../../comments.context";
import { NoComments } from "./NoComments";
import { useIntl } from "react-intl";
import { SingleChannelView } from "./SingleChannelView/SingleChannelView";
import { MultiChannelViewAppRedesign } from "./MultiChannelViewAppRedesign";
import { useCommsNewEndpoints } from "@certa/common/toggles";
import type { InfiniteConversationCommsModel } from "./ConversationListItemAppRedesign";
import type { ConversationCommsModel } from "@certa/queries/queries/comms";
import { commentsServices } from "@certa/queries/services/comments.services";
import type { GetCommentsParams } from "@certa/queries/types/comments.types";

// Add this helper function at the top of the file
const transformInfiniteQueryData = (
  data: InfiniteConversationCommsModel
): ConversationCommsModel => {
  if (!data?.data?.pages?.length) {
    return {
      results: [],
      total: 0,
      count: 0,
      next: false
    };
  }

  return {
    results: data.data.pages.flatMap(page => page.results),
    total: data.data.pages[0].total,
    count: data.data.pages.reduce((acc, page) => acc + page.count, 0),
    next: !!data.hasNextPage
  };
};

const ChannelViewIdentifier: FC<{
  channels: CommentChannel[];
  threadsComms: InfiniteConversationCommsModel;
  defaultStatus?: DropdownStatusDefault;
  defaultAdjudicationCode?: AdjudicationStatus;
  adjudicationRiskCodes?: string[];
  multiChannelWithTabs?: boolean;
}> = ({
  channels,
  threadsComms,
  defaultStatus,
  defaultAdjudicationCode,
  adjudicationRiskCodes,
  multiChannelWithTabs
}) => {
  const intl = useIntl();

  const transformedThreadsComms = transformInfiniteQueryData(threadsComms);

  const [threadWithLatestMessage, setLatestMessageThread] =
    useState<null | Number>(null);
  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  const {
    isMultiChannel,
    defaultThreadId,
    activeChannel,
    activeConversationThreadComms,
    setActiveChannel,
    setActiveConversationThreadComms
  } = useCommentsContext();

  const totalMessagesInAllChannels = useMemo(
    () =>
      channels.reduce(
        (total: number, channelContext: CommentChannel) =>
          total +
          channelContext.allThreads.reduce(
            (total, threadMeta) => total + threadMeta.messageCount,
            0
          ),
        0
      ),
    [channels]
  );

  const defaultActiveThread = useMemo(() => {
    const channel = activeChannel || channels[0];

    if (defaultThreadId) {
      const threadId = defaultThreadId;
      if (channel?.allThreads.find(thread => thread.threadId === threadId)) {
        return threadId;
      }
    } else {
      return threadWithLatestMessage || channel?.allThreads[0]?.threadId;
    }
  }, [activeChannel, channels, defaultThreadId, threadWithLatestMessage]);

  useEffect(() => {
    if (
      !isCommsNewEndpointEnabled ||
      !activeConversationThreadComms ||
      activeConversationThreadComms?.conversationId ||
      !threadsComms ||
      !defaultActiveThread
    )
      return;

    const thread = transformedThreadsComms.results.find(
      thread => thread.kryptonId === defaultActiveThread
    );

    if (!thread) return;

    setActiveConversationThreadComms({
      ...activeConversationThreadComms,
      conversationId: thread.id
    });
  }, [
    isCommsNewEndpointEnabled,
    activeConversationThreadComms,
    threadsComms,
    defaultActiveThread,
    setActiveConversationThreadComms,
    transformedThreadsComms.results
  ]);

  const getPayload = (
    objectId: number,
    objectType: CommentObjectTypes,
    uid?: string
  ) => {
    if (!objectId || !objectType) {
      // missing objectId or bad objectType
      return null;
    }

    // Prepare the payload
    const getCommentsPayload: Partial<GetCommentsParams> = {
      object_id: objectId,
      type: objectType
    };
    // comments-field-5215114
    // Add additional payload for the integration case
    if (objectType === CommentObjectTypes.INTEGRATION_RESULT) {
      getCommentsPayload["uid"] = uid;
      getCommentsPayload["field_id"] = objectId;
      getCommentsPayload["object_id"] = 1;
    }

    return getCommentsPayload;
  };

  const handleChannelSelect = (
    channel: CommentChannel,
    threadWithLatestMessage: number
  ) => {
    setActiveChannel(channel);
    setLatestMessageThread(threadWithLatestMessage);

    // Make call to channels API to mark unread comments as read
    commentsServices.getComments(
      getPayload(
        channel.objectId || 0,
        channel.objectType || CommentObjectTypes.STEP,
        channel.integrationUID
      ) as GetCommentsParams
    );
  };

  const handleChannelSelectComms = (conversationThread: ConversationThread) => {
    setActiveConversationThreadComms(conversationThread);

    // Find the corresponding thread ID for this conversation and update threadWithLatestMessage
    if (threadsComms && conversationThread.conversationId) {
      const thread = transformedThreadsComms.results.find(
        thread => thread.id === conversationThread.conversationId
      );

      if (thread && thread.kryptonId) {
        setLatestMessageThread(thread.kryptonId);

        // Find and set the active channel based on the thread's kryptonId
        const channelWithThread = channels.find(channel =>
          channel.allThreads.some(
            threadMeta => threadMeta.threadId === thread.kryptonId
          )
        );

        if (channelWithThread) {
          setActiveChannel(channelWithThread);
        }
      }
    }

    // Make call to channels API to mark unread comments as read
    commentsServices.getComments(
      getPayload(
        Number(conversationThread.objectId) || 0,
        (conversationThread.objectType as CommentObjectTypes) ||
          CommentObjectTypes.STEP
      ) as GetCommentsParams
    );
  };

  const noCommentsMessage = intl.formatMessage({
    id: "comments.noCommentsInThisView",
    defaultMessage: "There are no comments in this view."
  });

  /**
   * If there are no channels
   * - OR
   * If the view is multi-channel and total messages in all channels=>threads is 0
   * - Then
   * Render the no comments UI
   */
  if (!channels.length || (isMultiChannel && totalMessagesInAllChannels === 0))
    return <NoComments text={noCommentsMessage} isMultiChannel />;

  if (!isCommsNewEndpointEnabled && isMultiChannel && !activeChannel) {
    return (
      <MultiChannelViewAppRedesign
        channels={channels}
        threadsComms={threadsComms}
        handleChannelSelect={handleChannelSelect}
        handleChannelSelectComms={handleChannelSelectComms}
        multiChannelWithTabs={multiChannelWithTabs}
      />
    );
  }

  if (
    isCommsNewEndpointEnabled &&
    isMultiChannel &&
    (!activeConversationThreadComms ||
      activeConversationThreadComms.objectType ===
        CommentObjectTypes.CONSOLIDATED)
  ) {
    return (
      <MultiChannelViewAppRedesign
        channels={channels}
        threadsComms={threadsComms}
        handleChannelSelect={handleChannelSelect}
        handleChannelSelectComms={handleChannelSelectComms}
        multiChannelWithTabs={multiChannelWithTabs}
      />
    );
  }

  return (
    <SingleChannelView
      channel={activeChannel || channels[0]}
      activeConversationThreadComms={activeConversationThreadComms}
      defaultActiveThread={defaultActiveThread as number}
      defaultStatus={defaultStatus}
      defaultAdjudicationCode={defaultAdjudicationCode}
      adjudicationRiskCodes={adjudicationRiskCodes}
      threadsComms={transformedThreadsComms}
    />
  );
};

export { ChannelViewIdentifier };
